import { ref, computed, watch, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import type { ITuitionFeeRange } from '@app/interfaces/dtos/ITuitionFeeRange';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import { SelectListItemStateType } from "@domain/enums/SelectListItemStateType";
import debounce from '@utils/debounce';
import { useCurrency } from '@composables/useCurrency';
import { useSearchStore } from '@stores/useSearchStore';
import { UseTuitionFeeOptions, UseTuitionFeeReturn } from '@app/interfaces/useTuitionFee.interface';
import { FilterType } from '@domain/enums/FilterType';
import { TuitionFeeFilterValue } from '@studyportals/search-api-interface';
import { TuitionFeeFilterMapper } from '@app/routing/mappers/TuitionFeeFilterMapper';
import { useRouter } from 'vue-router';

export const useTuitionFee = (options: UseTuitionFeeOptions = {}): UseTuitionFeeReturn => {
	const { maximumTuitionFee = 50000, onTuitionFeeRangeChange } = options;

	const searchStore = useSearchStore();
	const { filtersSelection } = storeToRefs(searchStore);
	const router = useRouter();
	const currency = useCurrency();
	const mapper = TuitionFeeFilterMapper.getInstance();

	const minTuitionFee = 0;
	const minValue = ref(minTuitionFee);
	const maxValue = ref(maximumTuitionFee);
	const propsMaximumTuitionFee = ref(maximumTuitionFee);

	const sliderValue = computed({
		get: () => [minValue.value, maxValue.value],
		set: (val: number[]) => {
			minValue.value = val[0] ?? 0;
			maxValue.value = val[1] ?? maximumTuitionFee;
		}
	});

	const radioSelectItem = reactive({
		value: 'affordable-studies',
		text: 'Affordable Studies',
		state: SelectListItemStateType.ENABLED
	} as ISelectItem);


	watch(currency.userCurrency, async (newCurrency, oldCurrency) => {
		if (oldCurrency && newCurrency !== oldCurrency) {
			await convertValuesToNewCurrency(newCurrency, oldCurrency);
		}
	});

	watch(filtersSelection, (newSelection, oldSelection) => {
		const hadTuition = oldSelection?.some((filter) => filter.type === FilterType.TUITION_FEE) ?? false;
		const hasTuition = newSelection?.some((filter) => filter.type === FilterType.TUITION_FEE) ?? false;

		if (hadTuition && !hasTuition) {
			radioSelectItem.state = SelectListItemStateType.ENABLED;
			minValue.value = minTuitionFee;
			maxValue.value = propsMaximumTuitionFee.value;
		}
	});

	const convertValuesToNewCurrency = async (newCurrency: string, oldCurrency: string): Promise<void> => {
		const convertedMin = await currency.currencyConvert(minValue.value, oldCurrency, newCurrency);
		const convertedMax = await currency.currencyConvert(maxValue.value, oldCurrency, newCurrency);
		const convertedMaxProp = await currency.currencyConvert(maximumTuitionFee, 'EUR', newCurrency);

		if (convertedMin !== undefined) {
			minValue.value = Math.round(convertedMin);
		}
		if (convertedMax !== undefined) {
			maxValue.value = Math.round(convertedMax);
		}
		if (convertedMaxProp !== undefined) {
			propsMaximumTuitionFee.value = Math.round(convertedMaxProp);
		}

		if (radioSelectItem.state === SelectListItemStateType.SELECTED) {
			const mappedAffordable = mapper.getExtendedFilterValueById(TuitionFeeFilterValue.ZERO_TO_TWO_THOUSAND_FIVE_HUNDRED);
			if (mappedAffordable) {
				await updateFiltersSelection(mappedAffordable.id, mappedAffordable);
				return;
			}
		}

		debouncedEmitRangeChange();
	}

	const changeCurrencyClicked = (): void => {
		currency.triggerCurrencyChange();
	};

	const handleSliderChange = (value: number[]): void => {
		radioSelectItem.state = SelectListItemStateType.ENABLED;
		minValue.value = value[0] ?? 0;
		maxValue.value = value[1] ?? maximumTuitionFee;
		debouncedEmitRangeChange();
	};

	const handleMinFieldInput = (event: Event): void => {
		const target = event.target as HTMLInputElement;
		const parsed = parseInt(target.value.replace(/\D/g, ''), 10);
		if (isNaN(parsed)) return;

		radioSelectItem.state = SelectListItemStateType.ENABLED;
		const clampedValue = Math.min(Math.max(minTuitionFee, parsed), maxValue.value);
		if (minValue.value === clampedValue) {
			// Force input sync when min value hasn't changed
			target.value = String(clampedValue);
		}
		minValue.value = clampedValue;
		debouncedEmitRangeChange();
	};

	const handleMaxFieldInput = (event: Event): void => {
		const target = event.target as HTMLInputElement;
		const parsed = parseInt(target.value.replace(/[^\d]/g, ''), 10);
		if (isNaN(parsed)) return;

		radioSelectItem.state = SelectListItemStateType.ENABLED;
		const clampedValue = Math.max(Math.min(propsMaximumTuitionFee.value, parsed), minValue.value);
		if (maxValue.value === clampedValue) {
			// Force input sync when max value hasn't changed
			target.value = maxValue.value >= propsMaximumTuitionFee.value ? `+${propsMaximumTuitionFee.value}` : String(clampedValue);
		}
		maxValue.value = clampedValue;
		debouncedEmitRangeChange();
	}

	const updateFiltersSelection = async (id: string, mappedValue?: { id: string; virtualPath?: string; name?: string }): Promise<void> => {
		const currentSelection = filtersSelection.value.find((filter) => filter.type === FilterType.TUITION_FEE);
		let nextFilters = [...filtersSelection.value];

		if (currentSelection) {
			const updatedSelection = { ...currentSelection, values: [mappedValue ?? { id }] };
			nextFilters = nextFilters.map((filter) => filter.type === FilterType.TUITION_FEE ? updatedSelection : filter);
		} else {
			nextFilters = [...nextFilters, { type: FilterType.TUITION_FEE, values: [mappedValue ?? { id }] }];
		}

		filtersSelection.value = nextFilters;
		await searchStore.setRouteFromFilters(router);
		await searchStore.loadPageData(router);
	};

	const emitRangeChange = async (): Promise<void> => {
		const minEUR = await currency.currencyConvert(minValue.value, currency.userCurrency.value, 'EUR') ?? minValue.value;
		const maxForFilter = maxValue.value >= propsMaximumTuitionFee.value ? -1 : maxValue.value;
		const maxEUR = maxForFilter === -1 ? -1 : await currency.currencyConvert(maxForFilter, currency.userCurrency.value, 'EUR') ?? maxForFilter;

		const isStateInit = filtersSelection.value.filter((f) => f.type === FilterType.TUITION_FEE).length === 0;
		const tuitionFeeRange: ITuitionFeeRange = {
			min: Math.round(minEUR),
			max: maxEUR === -1 ? -1 : Math.round(maxEUR)
		};

		searchStore.setTuitionFeeRange(tuitionFeeRange);

		if (onTuitionFeeRangeChange) {
			onTuitionFeeRangeChange(tuitionFeeRange);
		}
		
		if (tuitionFeeRange.min === 0 && tuitionFeeRange.max === -1 && !isStateInit) {
			const nextFilters = filtersSelection.value.filter((filter) => filter.type !== FilterType.TUITION_FEE);
			filtersSelection.value = nextFilters;
			await searchStore.setRouteFromFilters(router);
			await searchStore.loadPageData(router);
		} else if (tuitionFeeRange.min !== 0 || tuitionFeeRange.max !== -1) {
			const filterId = JSON.stringify([tuitionFeeRange.min, tuitionFeeRange.max]);
			await updateFiltersSelection(filterId);
		}
	};

	const debouncedEmitRangeChange = debounce(() => {
		void emitRangeChange();
	}, 300);

	const handleAffordableStudiesChange = async (state: string): Promise<void> => {
		radioSelectItem.state = state as SelectListItemStateType;
		if (state === SelectListItemStateType.SELECTED as string) {
			minValue.value = minTuitionFee;
			const affordableAmount = await currency.currencyConvert(2500, 'EUR', currency.userCurrency.value);
			maxValue.value = affordableAmount ? Math.round(affordableAmount) : 2500;

			const mappedAffordable = mapper.getExtendedFilterValueById(TuitionFeeFilterValue.ZERO_TO_TWO_THOUSAND_FIVE_HUNDRED);
			searchStore.setTuitionFeeRange({ min: 0, max: 2500 });
			if (onTuitionFeeRangeChange) {
				onTuitionFeeRangeChange({ min: 0, max: 2500 });
			}

			if (mappedAffordable) {
				await updateFiltersSelection(mappedAffordable.id, mappedAffordable);
			} else {
				debouncedEmitRangeChange();
			}
		}
	};

	const initialiseFromFilters = async (): Promise<void> => {
		const tuitionFilter = filtersSelection.value.find((filter) => filter.type === FilterType.TUITION_FEE);
		const existingValue = tuitionFilter?.values?.[0];
		if (!existingValue) return;

		if (existingValue.id === TuitionFeeFilterValue.ZERO_TO_TWO_THOUSAND_FIVE_HUNDRED as string) {
			radioSelectItem.state = SelectListItemStateType.SELECTED;
			const affordableAmount = await currency.currencyConvert(2500, 'EUR', currency.userCurrency.value);
			minValue.value = minTuitionFee;
			maxValue.value = affordableAmount ? Math.round(affordableAmount) : 2500;
			searchStore.setTuitionFeeRange({ min: 0, max: 2500 });
			return;
		}

		try {
			const [min, max] = JSON.parse(existingValue.id) as [number, number];
			const convertedMin = await currency.currencyConvert(min, 'EUR', currency.userCurrency.value);
			const convertedMax = max === -1 ? propsMaximumTuitionFee.value : await currency.currencyConvert(max, 'EUR', currency.userCurrency.value);

			minValue.value = Math.round(convertedMin ?? min);
			maxValue.value = max === -1 ? propsMaximumTuitionFee.value : Math.round(convertedMax ?? max);
			radioSelectItem.state = SelectListItemStateType.ENABLED;
			searchStore.setTuitionFeeRange({ min, max });
		} catch {
			// ignore malformed value
		}
	};
	void initialiseFromFilters();

	// Computed values for display
	const displayMinValue = computed(() => minValue.value);
	const displayMaxValue = computed(() =>
		maxValue.value >= propsMaximumTuitionFee.value
			? `+${propsMaximumTuitionFee.value}`
			: maxValue.value
	);

	return {
		// State
		userCurrency: currency.userCurrency,
		sliderValue,
		minValue: displayMinValue,
		maxValue: displayMaxValue,
		minTuitionFee,
		propsMaximumTuitionFee,
		radioSelectItem,
		isProfileQuestionnairesReady: ref(searchStore.isProfileQuestionnairesReady),

		// Actions
		changeCurrencyClicked,
		handleSliderChange,
		handleMinFieldInput,
		handleMaxFieldInput,
		handleAffordableStudiesChange
	};
};
